import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeTab, setActiveTab] = useState('profile'); // profile | password
  
  // Master data
  const [cities, setCities] = useState([]);
  const [birthCities, setBirthCities] = useState([]);
  const [genders, setGenders] = useState([]);
  const [interests, setInterests] = useState([]);
  const [schools, setSchools] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  
  const [formData, setFormData] = useState({
    nama: '',
    username: '',
    nik: '',
    foto: '',
    no_telp: '',
    m_jeniskelamin_id: '',
    tgl_lahir: '',
    m_kota_id_lahir: '',
    m_kota_id: '',
    jenjang: '',
    m_sekolah_id: '',
    m_interest_id: '',
    nama_series: '',
    instagram: '',
    tiktok: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchMasterData();
    fetchUserProfile();
    fetchSeriesList();
  }, []);

  useEffect(() => {
    if (formData.jenjang && formData.nama_series) {
      fetchSchools();
    }
  }, [formData.jenjang, formData.nama_series, seriesList]);

  const fetchMasterData = async () => {
    try {
      const [citiesRes, gendersRes, interestsRes] = await Promise.all([
        axios.get(`${API}/cities`),
        axios.get(`${API}/genders`),
        axios.get(`${API}/interests`)
      ]);
      
      const sortedCities = (citiesRes.data || []).sort((a, b) => (a.kota || '').localeCompare(b.kota || ''));
      setCities(sortedCities);
      setBirthCities(sortedCities); // Same cities for birth
      setGenders(gendersRes.data);
      setInterests(interestsRes.data);
    } catch (error) {
      toast.error('Gagal memuat data master');
    }
  };

  const fetchSeriesList = async () => {
    try {
      const response = await axios.get(`${API}/series`);
      setSeriesList(response.data);
    } catch (error) {
      console.error('Error fetching series:', error);
    }
  };

  const fetchSchools = async () => {
    try {
      const response = await axios.get(`${API}/schools`);

      // Get kota from selected series or extract from nama_series
      const selectedSeries = seriesList.find(s => s.nama_series === formData.nama_series);
      const cityName = selectedSeries?.kota || formData.nama_series.replace(/\s*Series$/i, '').trim();

      // Filter by city name and jenjang
      const filtered = response.data.filter(s => {
        const matchCity = s.kota === cityName;
        const matchJenjang = s.jenjang === formData.jenjang;
        return matchCity && matchJenjang;
      });

      setSchools(filtered);
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const userData = response.data;
      setFormData({
        nama: userData.nama || '',
        username: userData.username || '',
        nik: userData.nik || '',
        foto: userData.foto || '',
        no_telp: userData.no_telp || '',
        m_jeniskelamin_id: userData.m_jeniskelamin_id || '',
        tgl_lahir: userData.tgl_lahir ? userData.tgl_lahir.split('T')[0] : '',
        m_kota_id_lahir: userData.m_kota_id_lahir || '',
        m_kota_id: userData.m_kota_id || '',
        jenjang: userData.jenjang || '',
        m_sekolah_id: userData.m_sekolah_id || '',
        m_interest_id: userData.m_interest_id || '',
        nama_series: userData.nama_series || '',
        instagram: userData.instagram || '',
        tiktok: userData.tiktok || ''
      });
      
      if (userData.foto) {
        setImagePreview(userData.foto);
      }
    } catch (error) {
      console.error('Fetch profile error:', error);
      
      // Safely extract error message
      let errorMessage = 'Gagal memuat data profil';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 2MB');
      return;
    }
    
    // Compress and convert to base64
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      const maxSize = 800;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      // Try different quality levels to get under 400KB
      let quality = 0.8;
      let base64;
      
      do {
        base64 = canvas.toDataURL('image/jpeg', quality);
        quality -= 0.1;
      } while (base64.length > 400 * 1024 && quality > 0.1); // 400KB limit
      
      setFormData(prev => ({ ...prev, foto: base64 }));
      setImagePreview(base64);
      toast.success('Foto berhasil diupload');
    };
    
    img.src = URL.createObjectURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      await axios.put(`${API}/auth/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Profil berhasil diupdate');
      await refreshUser(); // Refresh user context
    } catch (error) {
      console.error('Profile update error:', error);
      
      // Safely extract error message
      let errorMessage = 'Gagal update profil';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Password baru dan konfirmasi tidak sama');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password berhasil diubah');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Password change error:', error);
      
      let errorMessage = 'Gagal mengubah password';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white shadow-md py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/home')}
              className="text-ltb-bright hover:text-ltb-medium"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Profile Saya</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-all ${
                activeTab === 'profile'
                  ? 'border-b-2 border-ltb-bright text-ltb-bright'
                  : 'text-gray-600 hover:text-ltb-bright'
              }`}
            >
              📝 Update Profil
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 py-4 px-6 text-center font-medium transition-all ${
                activeTab === 'password'
                  ? 'border-b-2 border-ltb-bright text-ltb-bright'
                  : 'text-gray-600 hover:text-ltb-bright'
              }`}
            >
              🔒 Ubah Password
            </button>
          </div>
        </div>

        {activeTab === 'profile' && (
          <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo Upload */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Foto Profil</h2>
            <div className="flex items-center space-x-6">
              <div className="relative">
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt="Profile" 
                    className="w-24 h-24 rounded-full object-cover border-4 border-ltb-bright"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-300">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-sm text-gray-500 mt-1">Foto profile maksimal 2MB</p>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Informasi Dasar</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Email tidak dapat diubah</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Username tidak dapat diubah</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Lengkap <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nama"
                  value={formData.nama}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NIK
                </label>
                <input
                  type="text"
                  name="nik"
                  value={formData.nik}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  No. Telepon <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="no_telp"
                  value={formData.no_telp}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Jenis Kelamin <span className="text-red-500">*</span>
                </label>
                <select
                  name="m_jeniskelamin_id"
                  value={formData.m_jeniskelamin_id}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih jenis kelamin</option>
                  {genders.map(g => (
                    <option key={g.id} value={g.id}>{g.jeniskelamin}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tanggal Lahir <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="tgl_lahir"
                  value={formData.tgl_lahir}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kota Lahir <span className="text-red-500">*</span>
                </label>
                <select
                  name="m_kota_id_lahir"
                  value={formData.m_kota_id_lahir}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih kota lahir</option>
                  {birthCities.map(c => (
                    <option key={c.id} value={c.id}>{c.kota}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Location & Education */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Lokasi & Pendidikan</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kota Domisili <span className="text-red-500">*</span>
                </label>
                <select
                  name="m_kota_id"
                  value={formData.m_kota_id}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih kota domisili</option>
                  {cities.map(c => (
                    <option key={c.id} value={c.id}>{c.kota}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kota Series <span className="text-red-500">*</span>
                </label>
                <select
                  name="nama_series"
                  value={formData.nama_series}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih kota series</option>
                  {seriesList.map(s => (
                    <option key={s.id} value={s.nama_series}>{s.kota ? `${s.kota} Series` : s.nama_series}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Jenjang <span className="text-red-500">*</span>
                </label>
                <select
                  name="jenjang"
                  value={formData.jenjang}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih jenjang</option>
                  <option value="SD">SD/MI</option>
                  <option value="SMP">SMP/MTs</option>
                  <option value="SMA">SMA/SMK/MA</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nama Sekolah
                </label>
                {formData.jenjang && formData.m_kota_id ? (
                  <select
                    name="m_sekolah_id"
                    value={formData.m_sekolah_id}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                  >
                    <option value="">Pilih nama sekolah</option>
                    {schools.map(s => (
                      <option key={s.id} value={s.id}>{s.nama_sekolah}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg">
                    ⚠️ Pilih jenjang dan kota domisili terlebih dahulu
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minat <span className="text-red-500">*</span>
                </label>
                <select
                  name="m_interest_id"
                  value={formData.m_interest_id}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                >
                  <option value="">Pilih minat</option>
                  {interests.map(i => (
                    <option key={i.id} value={i.id}>{i.interest}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Media Sosial</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instagram
                </label>
                <input
                  type="text"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  placeholder="@username"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TikTok
                </label>
                <input
                  type="text"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleChange}
                  placeholder="@username"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                />
              </div>
            </div>
          </div>

            {/* Submit Button */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <button
                type="submit"
                disabled={updating}
                className="w-full bg-ltb-bright text-white py-3 px-6 rounded-lg font-semibold hover:bg-ltb-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? 'Menyimpan...' : 'Update Profil'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'password' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Ubah Password</h2>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password Lama <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password Baru <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                  minLength="6"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Konfirmasi Password Baru <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ltb-bright focus:border-transparent"
                  minLength="6"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-all"
              >
                Ubah Password
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;